import React, { useCallback, useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import DualListBoxGroup from "../Common/DualListBoxGroup";
import { GOOGLE_FEED } from "../../Common/constants";
import { ContainerLoader } from "../Promotions/PromotionsForm";
import { FullWidthLoader } from "../../../../components/full-width-loader";
import { useIntl, defineMessages, FormattedMessage } from "react-intl";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import { format } from "date-fns";
import { useConfig } from "../../../../lib/config";
import { useHttpClient } from "../../../../lib/http";
import { logSafeError } from "../../../../lib/safeLog";
import { StyledSpinner, StyledSpinnerButton } from "../EntityForm/EntityForm";
import { withAuthorizationVerifier } from "../../../../components/authorization-verifier";

const messages = defineMessages({
  confirmation: {
    id: "app.entityList.confirmation",
    description: "Confirmation Question",
    defaultMessage: "Are you sure?",
  },
  swalConfirm: {
    id: "googleFeed.swal.confirm",
    defaultMessage: "Yes",
  },
  swalCancel: {
    id: "googleFeed.swal.cancel",
    defaultMessage: "No",
  },
  nonPublishedTickers: {
    id: "googleFeed.header.nonPublishedTickers",
    defaultMessage: "Non published tickers",
  },
  publishedTickers: {
    id: "googleFeed.header.publishedTickers",
    defaultMessage: "Published tickers",
  },
  download: {
    id: "googleFeed.download",
    defaultMessage: "Dowloand",
  },
  manual: {
    id: "googleFeed.manual",
    defaultMessage: "Manual",
  },
  manualDescription: {
    id: "googleFeed.manual.description",
    defaultMessage: "to upload file to google feed",
  },
});

const StyledContainerDualListBoxGroup = styled.div`
  .rdl-move-all {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  button {
    margin-bottom: 50px;
  }
`;

const ManualLink = styled.div`
  margin-bottom: 20px;
`;

const Link = styled.a`
  text-decoration: underline !important;
`;

function GoogleFeed() {
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState([]);
  const [originalvalue, setOriginalValue] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmit, setIsSubmit] = useState(false);
  const intl = useIntl();
  const config = useConfig();
  // The feed is global and this section has no property context, so it authenticates with the
  // plain Keycloak access token instead of a property RPT. Memoized because useHttpClient keys
  // its client off the options reference and the fetch effect below depends on the client.
  const httpClientOptions = useMemo(() => ({ useNonRptToken: true }), []);
  const httpClient = useHttpClient(config.apiEndpoint, httpClientOptions);

  const field = useMemo(
    () => ({
      fieldName: "tickers",
      options: options,
      leftListHeader: intl.formatMessage(messages.nonPublishedTickers),
      rightListHeader: intl.formatMessage(messages.publishedTickers),
      disabled: isSubmit,
    }),
    [intl, isSubmit, options]
  );

  useEffect(() => {
    (async () => {
      try {
        const data =
          (await httpClient.get("/api/integrations/google/hotelAds/feed/properties")) || {};
        const newValue = data[GOOGLE_FEED.PUBLISHED_TICKERS];
        const newOptions = Object.keys(data).reduce((allOptions, type) => {
          const dataByType =
            data[type].map(ticker => {
              return {
                label: ticker,
                value: ticker,
              };
            }) || [];
          allOptions = [...allOptions, ...dataByType];
          return allOptions;
        }, []);
        setOptions(newOptions);
        setValue(newValue);
        setOriginalValue(newValue);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        logSafeError("Google feed properties load failed", error);
      }
    })();
  }, [httpClient]);

  const getToAdd = useCallback(() => value.filter(element => !originalvalue.includes(element)), [
    originalvalue,
    value,
  ]);

  const getToDelete = useCallback(() => originalvalue.filter(element => !value.includes(element)), [
    originalvalue,
    value,
  ]);

  const handleOnClick = useCallback(async () => {
    try {
      const respSwal = await swal({
        closeOnClickOutside: false,
        closeOnEsc: false,
        title: intl.formatMessage(messages.confirmation),
        text: `
        Agregados: ${getToAdd()}
        -
        Eliminados: ${getToDelete()}
        `,
        icon: "warning",
        buttons: {
          cancel: intl.formatMessage(messages.swalCancel),
          confirm: {
            text: intl.formatMessage(messages.swalConfirm),
            closeModal: false,
            value: true,
          },
        },
        dangerMode: true,
      });

      if (respSwal) {
        setIsSubmit(true);
        const date = format(new Date(), "dd_MM_yyyy");
        const request = {
          [GOOGLE_FEED.TICKER_TO_PUBLISH]: value,
        };
        const response =
          (await httpClient.patch("/api/integrations/google/hotelAds/feed/properties", request)) ||
          {};
        const blob = response;
        const objectUrl = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", `hlf_live_hotels_${date}.xml`);
        document.body.appendChild(link);
        link.click();
        setIsSubmit(false);

        swal.close();
      } else {
        swal.close();
      }
    } catch (error) {
      logSafeError("Google feed publish failed", error);
      setIsSubmit(false);
    }
  }, [intl, getToAdd, getToDelete, value, httpClient]);

  const handleChange = useCallback(({ value }) => {
    setValue(value);
  }, []);

  if (isLoading) {
    return (
      <ContainerLoader>
        <FullWidthLoader />
      </ContainerLoader>
    );
  }
  return (
    <div className="content-wrapper">
      <div className="content-heading">Google Feed</div>
      <div className="panel panel-default">
        <ManualLink>
          <Link
            href="https://witbooking.atlassian.net/wiki/spaces/CLIENTES/pages/1559232580/Subir+XML+al+feed+de+hoteles+de+Google+GHA"
            target="_blank">
            <FormattedMessage {...messages.manual} />
          </Link>{" "}
          <FormattedMessage {...messages.manualDescription} />
        </ManualLink>

        <StyledContainerDualListBoxGroup>
          <DualListBoxGroup field={field} onChange={handleChange} value={value} />
        </StyledContainerDualListBoxGroup>

        {isSubmit ? (
          <StyledSpinnerButton type="submit" bsClass="btn btn-info" disabled>
            <StyledSpinner className="fa fa-spinner fa-spin" />
          </StyledSpinnerButton>
        ) : (
          <StyledButton className="btn btn-primary" onClick={handleOnClick}>
            <FormattedMessage {...messages.download} />
          </StyledButton>
        )}
      </div>
    </div>
  );
}

export default withAuthorizationVerifier(GoogleFeed);
